import { apiAddress } from "../../config/server";
import { ApiBase, GenericTransport } from "./baseApi";

const BankDetailsApiBase = `${apiAddress}/api/bank-details/`;
const AdminBankDetailsApiBase = `${apiAddress}/api/admin/bank-details/`;

export interface BankDetails {
  bsb: string;
  accountNumber: string;
  accountName: string;
  lastFourDigits?: string;
  bankName?: string;
  isValid?: boolean;
  validatedAt?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface BankDetailsSummary {
  hasDetails: boolean;
  lastFourDigits?: string;
  bankName?: string;
  isValid?: boolean;
  validatedAt?: string;
}

export interface ValidationError {
  row: number;
  externalIdentifier: string;
  errors: string[];
  bsbError?: string;
  accountNumberError?: string;
  accountNameError?: string;
  personError?: string;
}

export interface BulkValidationResult {
  valid: boolean;
  totalRecords: number;
  validRecords: number;
  invalidRecords: number;
  duplicateCount: number;
  errors: ValidationError[];
  summary: {
    bsbErrors: number;
    accountNumberErrors: number;
    accountNameErrors: number;
    personNotFoundErrors: number;
    duplicateErrors: number;
  };
}

export interface CSVRow {
  externalIdentifier: string;
  bsb: string;
  accountNumber: string;
  accountName: string;
}

export interface BulkImportPreviewData {
  totalRecords: number;
  validationResult: BulkValidationResult;
  sampleValidRecords: Array<{
    externalIdentifier: string;
    bsb: string;
    accountNumber: string;
    accountName: string;
    personExists: boolean;
  }>;
  stats: {
    successRate: number;
    commonErrors: { type: string; count: number }[];
  };
  parsedData: CSVRow[]; // Parsed CSV data for reuse in import
}

export interface BulkImportResult {
  processedRecords: number;
  successfulImports: number;
  failedImports: number;
  skippedRecords: number;
  validationReport?: BulkValidationResult;
  processingErrors: Array<{
    externalIdentifier: string;
    error: string;
  }>;
}

export interface CSVFormatValidation {
  fileName: string;
  delimiter: ',' | '\t';
  hasHeaders: boolean;
  sampleRows: string[];
}

export interface IBankDetailsAuditEntry {
  _id: string;
  personId: string;
  accessedBy: string;
  associationId: string;
  accessType: 'view' | 'update' | 'delete' | 'export';
  reason?: string;
  context?: {
    payEventId?: string;
    endpoint?: string;
    ipAddress?: string;
    [key: string]: any;
  };
  timestamp: string;
  success: boolean;
  error?: string;
  __v: number;
}

export interface IPersonSummary {
  _id: string;
  firstName: string;
  lastName: string;
  externalIdentifier: string;
  preferredName?: string;
  fullName: string;
  id: string;
  image?: string | null;
}

export interface IAuditLogApiResponse {
  success: boolean;
  data: IBankDetailsAuditEntry[];
  persons: IPersonSummary[];
  error?: string;
}

export interface IAuditLogFilters {
  personId?: string;
  accessedBy?: string;
  limit?: number;
  startDate?: Date;
  endDate?: Date;
  associationId?: string;
}

class BankDetailsApi extends ApiBase {
  
  /**
   * Get own bank details
   */
  static getBankDetails(associationId: string): Promise<BankDetails | null> {
    return BankDetailsApi.get<BankDetails | null>(`${BankDetailsApiBase}${associationId}`)
      .then((resp) => resp.data.data);
  }

  /**
   * Get own bank details summary
   */
  static getBankDetailsSummary(associationId: string): Promise<BankDetailsSummary> {
    return BankDetailsApi.get<BankDetailsSummary>(`${BankDetailsApiBase}${associationId}/summary`)
      .then((resp) => resp.data.data);
  }

  /**
   * Create or update own bank details
   */
  static createOrUpdateBankDetails(associationId: string, bankDetails: {
    bsb: string;
    accountNumber: string;
    accountName: string;
  }): Promise<any> {
    return BankDetailsApi.post<any>(`${BankDetailsApiBase}${associationId}`, bankDetails)
      .then((resp) => resp.data.data);
  }

  /**
   * Delete own bank details
   */
  static deleteBankDetails(associationId: string): Promise<{ deleted: boolean }> {
    return BankDetailsApi.delete<{ deleted: boolean }>(`${BankDetailsApiBase}${associationId}`)
      .then((resp) => resp.data.data);
  }

  /**
   * Get own bank details history
   */
  static getBankDetailsHistory(associationId: string, limit?: number): Promise<any[]> {
    const url = limit 
      ? `${BankDetailsApiBase}${associationId}/history?limit=${limit}`
      : `${BankDetailsApiBase}${associationId}/history`;
    
    return BankDetailsApi.get<any[]>(url)
      .then((resp) => resp.data.data);
  }

  /**
   * Validate bank details format
   */
  static validateBankDetails(associationId: string, bankDetails: {
    bsb: string;
    accountNumber: string;
    accountName: string;
  }): Promise<{
    valid: boolean;
    errors: string[];
    bsbInfo?: any;
  }> {
    return BankDetailsApi.post<any>(`${BankDetailsApiBase}${associationId}/validate`, bankDetails)
      .then((resp) => resp.data.data);
  }

  // Admin endpoints

  /**
   * Get person's bank details (admin/manager only)
   */
  static getPersonBankDetails(personId: string, associationId: string): Promise<BankDetails | null> {
    return BankDetailsApi.get<BankDetails | null>(`${AdminBankDetailsApiBase}${personId}/${associationId}`)
      .then((resp) => resp.data.data);
  }

  /**
   * Update person's bank details (admin/manager only)
   */
  static updatePersonBankDetails(
    personId: string, 
    associationId: string,
    bankDetails: {
      bsb: string;
      accountNumber: string;
      accountName: string;
      reason?: string;
    }
  ): Promise<any> {
    return BankDetailsApi.put<any>(`${AdminBankDetailsApiBase}${personId}/${associationId}`, bankDetails)
      .then((resp) => resp.data.data);
  }

  /**
   * Delete person's bank details (admin/manager only)
   */
  static deletePersonBankDetails(personId: string, associationId: string, reason?: string): Promise<{ deleted: boolean }> {
    return BankDetailsApi.delete<{ deleted: boolean }>(`${AdminBankDetailsApiBase}${personId}/${associationId}`, { reason })
      .then((resp) => resp.data.data);
  }

  /**
   * Get person's bank details history (admin/manager only)
   */
  static getPersonBankDetailsHistory(personId: string, associationId: string, limit?: number): Promise<any[]> {
    const url = limit 
      ? `${AdminBankDetailsApiBase}${personId}/${associationId}/history?limit=${limit}`
      : `${AdminBankDetailsApiBase}${personId}/${associationId}/history`;
    
    return BankDetailsApi.get<any[]>(url)
      .then((resp) => resp.data.data);
  }

  /**
   * Get audit log (admin only)
   */
  static getAuditLog(associationId: string, filters: IAuditLogFilters = {}): Promise<IAuditLogApiResponse> {
    const params = new URLSearchParams();
    
    // Add associationId as required parameter
    params.append('associationId', associationId);
    
    // Add filters to query string
    if (filters.personId) params.append('personId', filters.personId);
    if (filters.accessedBy) params.append('accessedBy', filters.accessedBy);
    if (filters.limit) params.append('limit', filters.limit.toString());
    if (filters.startDate) params.append('startDate', filters.startDate.toISOString());
    if (filters.endDate) params.append('endDate', filters.endDate.toISOString());

    const queryString = params.toString();
    const url = `${AdminBankDetailsApiBase}audit?${queryString}`;

    return BankDetailsApi.get<IAuditLogApiResponse>(url)
      .then((resp) => {
        if (!resp.data.success) {
          throw new Error(resp.data.error || 'Failed to fetch audit log');
        }
        return resp.data;
      })
      .catch((error) => {
        console.error('Failed to fetch audit log:', error);
        
        // Return consistent error structure
        return {
          success: false,
          data: [],
          persons: [],
          error: error instanceof Error ? error.message : 'Unknown error occurred',
        };
      });
  }

  /**
   * Health check (admin only)
   */
  static healthCheck(associationId: string): Promise<any> {
    return BankDetailsApi.get<any>(`${BankDetailsApiBase}${associationId}/health`)
      .then((resp) => resp.data.data);
  }

  // Bulk import endpoints

  /**
   * Validate CSV format and return preview (admin only)
   */
  static validateCSVFormat(file: File, associationId: string): Promise<CSVFormatValidation> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('associationId', associationId);

    return BankDetailsApi.post<CSVFormatValidation>(
      `${AdminBankDetailsApiBase}validate-csv`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    ).then((resp) => resp.data.data);
  }

  /**
   * Preview bulk import from CSV - validation only (admin only)
   */
  static previewBulkImport(file: File, associationId: string): Promise<BulkImportPreviewData> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('associationId', associationId);

    return BankDetailsApi.post<BulkImportPreviewData>(
      `${AdminBankDetailsApiBase}bulk-import/preview`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    ).then((resp) => {
      // Handle validation errors specially
      if (!resp.data.success && resp.data.error?.validationReport) {
        const error = new Error(resp.data.error.message || 'Validation failed');
        (error as any).validationReport = resp.data.error.validationReport;
        throw error;
      }
      return resp.data.data;
    });
  }

  /**
   * Bulk import bank details from parsed data (admin only)
   * This method uses the parsed data from the preview step
   */
  static bulkImportFromData(records: CSVRow[], associationId: string, reason?: string): Promise<BulkImportResult> {
    const requestBody = {
      records,
      reason,
      associationId
    };

    return BankDetailsApi.post<BulkImportResult>(
      `${AdminBankDetailsApiBase}bulk-import-data`,
      requestBody,
      {
        // Set longer timeout for bulk operations
        timeout: 5 * 60 * 1000, // 5 minutes
      }
    ).then((resp) => {
      // Handle validation errors specially
      if (!resp.data.success && resp.data.error?.validationReport) {
        const error = new Error(resp.data.error.message || 'Import failed');
        (error as any).validationReport = resp.data.error.validationReport;
        throw error;
      }
      return resp.data.data;
    }).catch((error) => {
      // Transform axios error to include validation data if present
      if (error.response?.data?.error?.validationReport) {
        const transformedError = new Error(error.response.data.error.message || 'Import failed');
        (transformedError as any).validationReport = error.response.data.error.validationReport;
        throw transformedError;
      }
      throw error;
    });
  }

  /**
   * Bulk import bank details from CSV (admin only)
   * Legacy method - uploads file directly
   */
  static bulkImport(file: File, associationId: string, reason?: string): Promise<BulkImportResult> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('associationId', associationId);
    if (reason) {
      formData.append('reason', reason);
    }

    return BankDetailsApi.post<BulkImportResult>(
      `${AdminBankDetailsApiBase}bulk-import`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        // Set longer timeout for bulk operations
        timeout: 5 * 60 * 1000, // 5 minutes
      }
    ).then((resp) => {
      // Handle validation errors specially
      if (!resp.data.success && resp.data.error?.validationReport) {
        const error = new Error(resp.data.error.message || 'Import failed');
        (error as any).validationReport = resp.data.error.validationReport;
        throw error;
      }
      return resp.data.data;
    }).catch((error) => {
      // Transform axios error to include validation data if present
      if (error.response?.data?.error?.validationReport) {
        const transformedError = new Error(error.response.data.error.message || 'Import failed');
        (transformedError as any).validationReport = error.response.data.error.validationReport;
        throw transformedError;
      }
      throw error;
    });
  }

  /**
   * Download CSV template with persons from association (admin only)
   */
  static async downloadCSVTemplate(
    associationId: string,
    filter: 'all' | 'without-bank-details' = 'all'
  ): Promise<void> {
    const queryParams = BankDetailsApi.prepareQueryParams({ 
      associationId,
      filter 
    });
    const url = `${AdminBankDetailsApiBase}csv-template${queryParams}`;
    
    try {
      // Use fetch with credentials to handle authentication
      const response = await fetch(url, {
        method: 'GET',
        credentials: 'include', // Include session cookies
        headers: {
          'Accept': 'text/csv',
        },
      });

      if (!response.ok) {
        const errorText = await response.text();
        let errorMessage = `HTTP ${response.status}: ${response.statusText}`;
        
        try {
          const errorData = JSON.parse(errorText);
          errorMessage = errorData.error?.message || errorData.message || errorMessage;
        } catch {
          // If not JSON, use the text as error message
          errorMessage = errorText || errorMessage;
        }
        
        throw new Error(errorMessage);
      }

      // Get filename from Content-Disposition header or create default
      const contentDisposition = response.headers.get('Content-Disposition');
      let filename = `bank_details_template_${filter}_${new Date().toISOString().split('T')[0]}.csv`;
      
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="?([^"]+)"?/);
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      }

      // Create blob and trigger download
      const blob = await response.blob();
      const downloadUrl = URL.createObjectURL(blob);
      
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = filename;
      link.style.display = 'none';
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Clean up the object URL
      URL.revokeObjectURL(downloadUrl);
      
    } catch (error) {
      throw new Error(`Template download failed: ${error.message}`);
    }
  }

  /**
   * Get CSV template info without downloading (admin only)
   */
  static async getCSVTemplateInfo(
    associationId: string,
    filter: 'all' | 'without-bank-details' = 'all'
  ): Promise<{
    personCount: number;
    filter: string;
    associationName?: string;
  }> {
    const queryParams = BankDetailsApi.prepareQueryParams({ 
      associationId,
      filter, 
      info: 'true' 
    });
    
    return BankDetailsApi.get<{
      personCount: number;
      filter: string;
      associationName?: string;
    }>(`${AdminBankDetailsApiBase}csv-template${queryParams}`)
    .then((resp) => resp.data.data);
  }
}

export default BankDetailsApi;
